import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getVideoPodcastById, updateVideoPodcast } from '../../api/videoPodcast';
import { useAuth } from '../../components/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import toast from 'react-hot-toast';

const EditVideoPodcastPage = () => {
  const { id } = useParams();
  const { currentUser, token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [language, setLanguage] = useState('en');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [isPublished, setIsPublished] = useState(true);
  const [existingVideo, setExistingVideo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const categories = ['General', 'Technology', 'Entertainment', 'Sports', 'Business', 'Health', 'Education', 'News', 'Lifestyle', 'Music', 'Comedy', 'Other'];

  const languages = {
    en: 'English',
    hi: 'Hindi',
    kn: 'Kannada',
    te: 'Telugu',
    ta: 'Tamil',
    ml: 'Malayalam',
    mr: 'Marathi',
    bn: 'Bengali',
    gu: 'Gujarati',
    pa: 'Punjabi',
  };

  useEffect(() => {
    if (!currentUser || !token) {
      toast.error('Please login to edit video podcasts');
      navigate('/login');
    }
  }, [currentUser, token, navigate]);

  useEffect(() => {
    const loadPodcast = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const podcast = await getVideoPodcastById(id);
        const ownerId = String(podcast.authorId?._id || podcast.authorId || '');
        if (currentUser && ownerId && ownerId !== String(currentUser.id)) {
          toast.error('You can only edit your own podcasts');
          navigate(`/video-podcasts/${id}`);
          return;
        }
        setTitle(podcast.title || '');
        setDescription(podcast.description || '');
        setCategory(podcast.category || 'General');
        setLanguage(podcast.language || 'en');
        setIsPublished(Boolean(podcast.isPublished));
        setExistingVideo(podcast.video || '');
        if (podcast.video && (podcast.video.startsWith('http://') || podcast.video.startsWith('https://'))) {
          setVideoUrl(podcast.video);
        }
      } catch (err) {
        setError(err.message || 'Failed to load podcast');
        toast.error(err.message || 'Failed to load podcast');
      } finally {
        setLoading(false);
      }
    };
    loadPodcast();
  }, [id, currentUser, navigate]);

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video file size should be less than 500MB');
      return;
    }
    setVideoFile(file);
    setVideoUrl('');
  };

  const handleThumbnailFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    setThumbnailFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setThumbnailPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentUser || !token) {
      navigate('/login');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('category', category);
      formData.append('language', language);
      formData.append('isPublished', isPublished ? 'true' : 'false');

      if (videoFile) {
        formData.append('video', videoFile);
      } else if (videoUrl.trim()) {
        formData.append('video', videoUrl.trim());
      }

      if (thumbnailFile) {
        formData.append('thumbnail', thumbnailFile);
      }

      const result = await updateVideoPodcast(id, formData, token);
      if (result?.success) {
        toast.success('Podcast updated successfully');
        navigate(`/video-podcasts/${id}`);
      } else {
        throw new Error(result?.message || 'Failed to update podcast');
      }
    } catch (err) {
      setError(err.message || 'Failed to update podcast');
      toast.error(err.message || 'Failed to update podcast');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl bg-[var(--bodyBackground)] p-4 text-[var(--textColor)]">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 rounded-full p-2 transition hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowBackIcon className="h-6 w-6" />
        </button>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          <VideoLibraryIcon className="text-blue-600" />
          Edit Video Podcast
        </h1>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/50">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
        <div>
          <label htmlFor="title" className="mb-2 block text-sm font-medium">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-700"
            required
            maxLength={200}
          />
        </div>

        <div>
          <label htmlFor="description" className="mb-2 block text-sm font-medium">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full resize-y rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-700"
            maxLength={1000}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="category" className="mb-2 block text-sm font-medium">Category</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-700"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="language" className="mb-2 block text-sm font-medium">Language</label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-700"
            >
              {Object.entries(languages).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            id="isPublished"
            type="checkbox"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="isPublished" className="text-sm font-medium">Published (visible in public feed)</label>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium">Replace Video (optional)</label>
          {existingVideo && !videoFile && !videoUrl && (
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Current video: {existingVideo}
            </p>
          )}
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoFileChange}
            className="w-full rounded-lg border border-gray-300 bg-white p-3 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 dark:border-gray-600 dark:bg-gray-700"
          />
          <div className="my-3 text-center text-gray-500">OR</div>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => {
              setVideoUrl(e.target.value);
              if (e.target.value) setVideoFile(null);
            }}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-700"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium">
            <ImageIcon className="mr-1 inline h-4 w-4" />
            Replace Thumbnail (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleThumbnailFileChange}
            className="w-full rounded-lg border border-gray-300 bg-white p-3 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 dark:border-gray-600 dark:bg-gray-700"
          />
          {thumbnailPreview && (
            <img src={thumbnailPreview} alt="Thumbnail preview" className="mt-4 h-48 max-w-xs rounded-lg object-cover" />
          )}
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/video-podcasts/${id}`)}
            disabled={isSubmitting}
            className="rounded-lg bg-gray-200 px-6 py-3 text-gray-700 transition hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditVideoPodcastPage;
